/**
 * PATH: packages/email-hub/src/feature/pages/inbox-page/InboxOverlays.tsx
 * FILE_ID: EMAIL-INBOX-OVERLAYS-001
 * PURPOSE: Preserve Inbox context menus, modals, and markup overlay.
 */

import type { ReactNode } from 'react'
import BulkLinkToJobModal from '../../components/BulkLinkToJobModal'
import LinkToJobModal from '../../components/LinkToJobModal'
import { MarkupDrawer } from '../../components/MarkupDrawer'
import type { InboxPageController } from './useInboxPageController'
import { AllocateBoardDialog } from '../../components/AllocateBoardDialog'
import { QuickTriagePalette } from './QuickTriagePalette'

export function InboxOverlays({
  vm,
  contextMenuNode,
}: {
  vm: InboxPageController
  contextMenuNode: ReactNode
}) {
  return (
    <>
      {contextMenuNode}

      {/* Bulk allocation modal — driven by the "Link to job" button
          in the bulk action bar. Uses the most recently focused
          email's subject as a hint so the modal's local fuzzy
          ordering surfaces the most likely job at the top. */}
      <BulkLinkToJobModal
        emailIds={Array.from(vm.selectedIds)}
        hintSubject={
          vm.selectedEmailId
            ? vm.emails.find(e => e.id === vm.selectedEmailId)?.subject ?? undefined
            : vm.emails.find(e => vm.selectedIds.has(e.id))?.subject ?? undefined
        }
        hintFrom={
          vm.selectedEmailId
            ? vm.emails.find(e => e.id === vm.selectedEmailId)?.from_address ?? undefined
            : vm.emails.find(e => vm.selectedIds.has(e.id))?.from_address ?? undefined
        }
        isOpen={vm.bulkLinkOpen}
        onClose={() => vm.setBulkLinkOpen(false)}
        onLinked={(jobId, count, meta) => {
          // Build a richer success message that surfaces the backend's
          // cross-mailbox dedup + auto-contact-registration extras (see
          // qb!2436 / organisation/services/email_job_linking). The
          // operator explicitly asked for this so they understand why
          // the count goes up beyond their selection (CC'd siblings)
          // and which side-effects fired.
          const parts = [
            `Linked ${count} email${count === 1 ? '' : 's'} to job #${jobId}`,
          ]
          const propagated = meta?.propagatedSiblings ?? 0
          if (propagated > 0) {
            parts.push(
              `+${propagated} sibling${propagated === 1 ? '' : 's'} in other mailboxes`,
            )
          }
          const contacts = meta?.contactsRegistered ?? 0
          if (contacts > 0) {
            parts.push(
              `${contacts} new client contact${contacts === 1 ? '' : 's'} registered`,
            )
          }
          if (meta?.primaryEmailSet) {
            parts.push('client primary email backfilled')
          }
          vm.toast(parts.join(' · '), 'success')
          vm.setSelectedIds(new Set())
          vm.refetchPreservingScroll()
        }}
      />

      {/* Link-to-Job modal for the AI Analysis tab — opened when the
          co-pilot's "link/create job" suggestion is clicked. Mirrors the
          reader's own modal so the flow is identical from either entry. */}
      {vm.aiLinkOpen && vm.selectedEmail && (
        <LinkToJobModal
          emailId={vm.selectedEmail.id}
          emailSubject={vm.selectedEmail.subject || '(no subject)'}
          emailBody={vm.selectedEmail.body_text || vm.selectedEmail.body_preview || ''}
          emailFrom={vm.selectedEmail.from_address || ''}
          isOpen
          onClose={() => vm.setAiLinkOpen(false)}
        />
      )}

      {/* Markup drawer — single instance shared by the three entry
          points (lightbox button, right-click menu, ⌘⇧M shortcut).
          ``anchorEmailId`` lights up the "Send as reply" button when
          the operator is currently previewing an email, so the
          marked-up screenshot lands in a reply to that thread.
          When the drawer was opened by the global shortcut from a
          list-only view, it stays in "new email" mode. Mounted
          INSIDE the render-prop function (not as a second child of
          EmailContextMenuProvider, which only accepts one
          render-fn child). */}
      <MarkupDrawer
        src={vm.markupSrc}
        anchorEmailId={vm.selectedEmailId}
        onClose={() => {
          if (vm.markupSrc?.startsWith('blob:')) URL.revokeObjectURL(vm.markupSrc)
          vm.setMarkupSrc(null)
        }}
      />

      <QuickTriagePalette vm={vm} />
      {vm.allocateBoardEmailId && (
        <AllocateBoardDialog
          emailId={vm.allocateBoardEmailId}
          alreadyAllocated={Boolean(
            vm.emails.find((row) => row.id === vm.allocateBoardEmailId)
              ?.allocation_summary,
          )}
          api={vm.kanbanApi}
          orgId={vm.kanbanOrgId ?? null}
          onClose={() => vm.setAllocateBoardEmailId(null)}
          onAllocated={(summary) => {
            const emailId = vm.allocateBoardEmailId
            if (!emailId) return
            if (summary) {
              vm.updateEmailLocal(emailId, { allocation_summary: summary })
              return
            }
            vm.refetchPreservingScroll()
          }}
        />
      )}
    </>
  )
}
